import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquare, Plus, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import type { OccurrenceEntry, OccurrencePhoto } from "@/domain/rdo/schema";

type OccurrencesModuleProps = {
  value: OccurrenceEntry[];
  onChange: (value: OccurrenceEntry[]) => void;
  mode: "create" | "edit" | "view";
  // Espelhamento: chamado com as fotos recém-adicionadas a uma ocorrência
  // (apenas quando hidePhotos = false), para refletir no container geral de Fotos.
  onMirrorPhotos?: (photos: OccurrencePhoto[], occurrenceIndex: number) => void;
};

const IMPACTS = ["Baixo", "Médio", "Alto"] as const;

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export function OccurrencesModule({ value, onChange, mode, onMirrorPhotos }: OccurrencesModuleProps) {
  const editable = mode !== "view";
  const occurrences = value ?? [];
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  const updateField = (index: number, field: keyof OccurrenceEntry, fieldValue: any) => {
    const next = occurrences.map((o, i) => (i === index ? { ...o, [field]: fieldValue } : o));
    onChange(next);
  };

  const addOccurrence = () => {
    const novo: OccurrenceEntry = {
      title: "",
      type: "",
      impact: "Baixo",
      responsible: "",
      description: "",
      actionTaken: "",
      hidePhotos: false,
      photos: [],
    };
    onChange([...occurrences, novo]);
  };

  const removeOccurrence = (index: number) => {
    onChange(occurrences.filter((_, i) => i !== index));
  };

  const addPhotos = async (index: number, files: FileList) => {
    const novas: OccurrencePhoto[] = [];
    for (const file of Array.from(files)) {
      try {
        const url = await readFileAsDataURL(file);
        novas.push({
          id: crypto.randomUUID?.() ?? String(Math.random()),
          url,
          caption: "",
          hiddenFromReport: false,
          createdAt: new Date().toISOString(),
        });
      } catch {
        // ignora arquivo com erro de leitura
      }
    }
    if (novas.length === 0) return;
    const current = occurrences[index];
    updateField(index, "photos", [...(current.photos ?? []), ...novas]);
    // Espelha no container geral de Fotos, salvo se a ocorrência estiver marcada
    // para ocultar (mesma regra do fluxo legado, avaliada no momento da adição).
    if (!current.hidePhotos) onMirrorPhotos?.(novas, index);
  };

  const pickPhotos = (index: number) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/jpg,image/png,image/heic";
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) addPhotos(index, files);
    };
    input.click();
  };

  const impactBadgeVariant = (impact?: string) =>
    impact === "Alto" ? "destructive" : impact === "Médio" ? "secondary" : "default";

  return (
    <Card data-section="occurrences">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Ocorrências ({occurrences.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editable ? (
          <div className="space-y-4">
            {occurrences.map((occurrence, index) => {
              const photos = occurrence.photos ?? [];
              const displayPhotos = photos.slice(0, 2);
              const remaining = Math.max(0, photos.length - 2);
              return (
                <div key={occurrence.id ?? index} className="border rounded-lg p-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">Ocorrência {index + 1}</h4>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => pickPhotos(index)}
                        aria-label="Enviar fotos da ocorrência"
                        className="h-8 w-8 p-0"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOccurrence(index)}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`occurrence-type-${index}`}>Tipo</Label>
                      <Input
                        id={`occurrence-type-${index}`}
                        value={occurrence.type ?? ""}
                        onChange={(e) => updateField(index, "type", e.target.value)}
                        placeholder="Ex: Segurança, Qualidade, Prazo"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`occurrence-impact-${index}`}>Impacto</Label>
                      <Select
                        value={occurrence.impact ?? "Baixo"}
                        onValueChange={(value) => updateField(index, "impact", value)}
                      >
                        <SelectTrigger id={`occurrence-impact-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {IMPACTS.map((imp) => (
                            <SelectItem key={imp} value={imp}>
                              {imp}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-2 grid md:grid-cols-2 gap-4 items-start">
                      <div>
                        <Label htmlFor={`occurrence-responsible-${index}`}>Responsável</Label>
                        <Input
                          id={`occurrence-responsible-${index}`}
                          value={occurrence.responsible ?? ""}
                          onChange={(e) => updateField(index, "responsible", e.target.value)}
                          placeholder="Ex: Técnico de Segurança"
                        />
                      </div>

                      {/* Miniaturas das fotos */}
                      {photos.length > 0 && (
                        <div className="flex items-end gap-2 flex-wrap">
                          {displayPhotos.map((photo, photoIndex) => (
                            <div
                              key={photo.id ?? photoIndex}
                              className="w-16 h-16 border rounded cursor-pointer hover:scale-105 transition-transform overflow-hidden bg-muted"
                              onClick={() => setGalleryIndex(index)}
                              title={photo.caption || "Ver foto"}
                            >
                              {photo.url ? (
                                <img src={photo.url} alt={photo.caption ?? ""} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                          ))}
                          {remaining > 0 && (
                            <div
                              className="w-16 h-16 border rounded cursor-pointer hover:scale-105 transition-transform bg-muted flex items-center justify-center text-sm font-medium"
                              onClick={() => setGalleryIndex(index)}
                              title={`Ver mais ${remaining} foto(s)`}
                            >
                              +{remaining}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor={`occurrence-description-${index}`}>Descrição</Label>
                    <Textarea
                      id={`occurrence-description-${index}`}
                      value={occurrence.description ?? ""}
                      onChange={(e) => updateField(index, "description", e.target.value)}
                      placeholder="Descreva detalhadamente a ocorrência"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor={`occurrence-action-${index}`}>Ação Tomada</Label>
                    <Textarea
                      id={`occurrence-action-${index}`}
                      value={occurrence.actionTaken ?? ""}
                      onChange={(e) => updateField(index, "actionTaken", e.target.value)}
                      placeholder="Descreva as ações tomadas para resolver a ocorrência"
                      rows={2}
                    />
                  </div>

                  {photos.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={occurrence.hidePhotos || false}
                        onCheckedChange={(checked) => updateField(index, "hidePhotos", checked)}
                      />
                      <Label className="text-sm">Ocultar fotos do container principal</Label>
                    </div>
                  )}
                </div>
              );
            })}

            {occurrences.length === 0 && (
              <div className="text-center text-muted-foreground py-8">Nenhuma ocorrência registrada</div>
            )}

            <Button variant="outline" onClick={addOccurrence} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Ocorrência
            </Button>
          </div>
        ) : occurrences.length > 0 ? (
          <div className="space-y-4">
            {occurrences.map((occurrence, index) => (
              <div key={occurrence.id ?? index} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  {occurrence.type && <Badge variant="outline">{occurrence.type}</Badge>}
                  {occurrence.impact && (
                    <Badge variant={impactBadgeVariant(occurrence.impact)}>Impacto {occurrence.impact}</Badge>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Descrição:</span> {occurrence.description}</div>
                  <div><span className="font-medium">Ação Tomada:</span> {occurrence.actionTaken}</div>
                  <div><span className="font-medium">Responsável:</span> {occurrence.responsible}</div>
                </div>
                {(occurrence.photos?.length ?? 0) > 0 && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    {occurrence.photos!.map((photo, photoIndex) => (
                      <div
                        key={photo.id ?? photoIndex}
                        className="w-16 h-16 border rounded overflow-hidden bg-muted cursor-pointer"
                        onClick={() => setGalleryIndex(index)}
                      >
                        {photo.url && <img src={photo.url} alt={photo.caption ?? ""} className="w-full h-full object-cover" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">Nenhuma ocorrência registrada.</p>
        )}
      </CardContent>

      {/* Galeria simples das fotos da ocorrência */}
      <Dialog open={galleryIndex !== null} onOpenChange={(open) => !open && setGalleryIndex(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Fotos da ocorrência {galleryIndex !== null ? galleryIndex + 1 : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {galleryIndex !== null &&
              (occurrences[galleryIndex]?.photos ?? []).map((photo, i) => (
                <div key={photo.id ?? i} className="border rounded overflow-hidden bg-muted aspect-square">
                  {photo.url && <img src={photo.url} alt={photo.caption ?? ""} className="w-full h-full object-cover" />}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
